import { useQuery } from "@tanstack/react-query";

let contentTypesPromise = null;

const readCachedContentTypes = () => {
  const cached = sessionStorage.getItem("contentTypes");
  if (!cached) return null;

  try {
    return JSON.parse(cached);
  } catch {
    sessionStorage.removeItem("contentTypes");
    return null;
  }
};

const writeCachedContentTypes = (contentTypes) => {
  sessionStorage.setItem("contentTypes", JSON.stringify(contentTypes));
  return contentTypes;
};

const fetchContentTypes = async (api, signal) => {
  const cached = readCachedContentTypes();
  if (cached) return cached;
  if (!api) return null;

  if (!contentTypesPromise) {
    contentTypesPromise = api
      .get("/content-types/", { signal })
      .then((response) => writeCachedContentTypes(response.data))
      .finally(() => {
        contentTypesPromise = null;
      });
  }

  return contentTypesPromise;
};

export const useContentTypes = (api) => {
  const query = useQuery({
    queryKey: ["content-types"],
    enabled: Boolean(api),
    queryFn: ({ signal }) => fetchContentTypes(api, signal),
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    initialData: readCachedContentTypes,
  });

  return {
    contentTypes: query.data,
    loading: query.isLoading,
    error: query.error,
  };
};

export const useContentType = (api, modelName) => {
  const { contentTypes, loading } = useContentTypes(api);

  return {
    contentTypeId: modelName ? contentTypes?.[modelName] ?? null : null,
    loading,
  };
};

export const getContentTypeId = (modelName) => {
  const contentTypes = readCachedContentTypes();
  return contentTypes?.[modelName] || null;
};

export const initializeContentTypes = async (api) => {
  try {
    return await fetchContentTypes(api);
  } catch (err) {
    if (err.response?.status === 401) {
      console.log("User not authenticated, content types not initialized");
    } else {
      console.error("Failed to initialize content types:", err);
    }
    return null;
  }
};
